import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import type { MobileMenuProps, NavigationView } from '../../types/navigation';

const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  navigation,
  currentView,
  onViewChange,
  onClose,
  userEmail
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const { signOut } = useAuth();

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  useEffect(() => {
    const loadProfilePhoto = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: photoData } = await supabase
          .from('profile_photos')
          .select('photo_url')
          .eq('user_id', user.id)
          .maybeSingle();

        if (photoData?.photo_url) {
          setPhotoUrl(photoData.photo_url);
        }
      } catch (error) {
        console.error('Error loading profile photo:', error);
      }
    };

    loadProfilePhoto();
  }, []);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(onClose, 200);
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-200 ease-out ${
          isOpen && isAnimating ? 'opacity-50' : 'opacity-0'
        }`}
        onClick={handleClose}
      />
      <div
        className={`fixed inset-y-0 right-0 w-[280px] bg-white shadow-xl transform transition-transform duration-200 ease-[cubic-bezier(0.4, 0, 0.2, 1)] ${
          isOpen && isAnimating ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt="Profile"
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="h-6 w-6 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              </div>
            )}
            <div className="flex-1">
              <p className="text-sm text-gray-600">{userEmail}</p>
            </div>
          </div>
        </div>

        <div className="space-y-1 px-2 pb-3 pt-2">
          {navigation.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-base font-medium ${
                  currentView === item.view
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:bg-gray-700 hover:text-white'
                }`}
                onClick={() => {
                  if (item.view) {
                    onViewChange(item.view);
                  }
                  onClose();
                }}
              >
                {Icon && <Icon className="h-5 w-5" />}
                {item.name}
              </Link>
            );
          })}
        </div>

        <div className="p-2 border-t border-gray-200">
          <button
            onClick={async () => {
              handleClose();
              await signOut();
            }}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg text-left transition-colors duration-100"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path fillRule="evenodd" d="M7.5 3.75A1.5 1.5 0 006 5.25v13.5a1.5 1.5 0 001.5 1.5h6a1.5 1.5 0 001.5-1.5V15a.75.75 0 011.5 0v3.75a3 3 0 01-3 3h-6a3 3 0 01-3-3V5.25a3 3 0 013-3h6a3 3 0 013 3V9A.75.75 0 0115 9V5.25a1.5 1.5 0 00-1.5-1.5h-6zm5.03 4.72a.75.75 0 010 1.06l-1.72 1.72h10.94a.75.75 0 010 1.5H10.81l1.72 1.72a.75.75 0 11-1.06 1.06l-3-3a.75.75 0 010-1.06l3-3a.75.75 0 011.06 0z" clipRule="evenodd" />
            </svg>
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileMenu;